import { DXG, useDataGrid } from 'components/devexpress/Grid/dx-grid';
import { Input } from 'components/uikit/inputs/Input';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import formStyles from 'components/form/form.module.scss';
import gridStyles from 'components/devexpress/Grid/grid.module.scss';
import { Button } from 'components/uikit/buttons/Button';
import { StringParam, useQueryParams } from 'react-router-url-params';
import {
  type IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/cjs/data-grid';
import { AppLink } from 'components/uikit/buttons/AppLink';
import { Links } from 'application/constants/links';
import { Loading } from 'components/uikit/suspense/Loading';
import { jsPDF } from 'jspdf';
import { exportDataGrid } from 'devextreme/pdf_exporter';
import autoTable from 'jspdf-autotable';
import { addHeader, fieldTitleCell } from 'helpers/pdf/pdf-helper';
import { convertAbasTable, getQueryHeadField } from 'helpers/abas-helper';
import { errorToString } from 'helpers/error-helpers';
import {
  EditorPreparingEvent,
  OptionChangedEvent,
} from 'devextreme/ui/data_grid';
import clsx from 'clsx';
import { Pagination, PaginationProps } from 'components/uikit/table/Pagination';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from 'components/uikit/Breadcrumbs';
import { ArticlesClient, ArticlesQuery } from 'services/api/api-client';
import PrintIcon from '../../../assets/icons/print-icon.svg';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from 'helpers/pagination-helper';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { useNavigate } from 'react-router';
import { sortHelper } from '../../../helpers/sort-helper.ts';
import { DataGridRef } from 'devextreme-react/data-grid';

type FormParams = {
  partNoFrom: string;
  partNoTo: string;
  supplierPartNoFrom: string;
  supplierPrtNoTo: string;
};

const pageSizes = [100, 200, 500];
export const MaxNumberOfShownPagesInPager = 11;

export const ArticlesPage = () => {
  const [numberOfPages, setNumberOfPages] = useState(1);
  useDataGrid();
  const [params, setParams] = useQueryParams({
    partNoFrom: StringParam,
    partNoTo: StringParam,
    supplierPartNoFrom: StringParam,
    supplierPrtNoTo: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
  });
  const form = useAdvancedForm<FormParams>(
    async (data) => {
      setParams(data);
    },
    {
      defaultValues: {
        partNoFrom: params.partNoFrom ?? '',
        partNoTo: params.partNoTo ?? '',
        supplierPartNoFrom: params.supplierPartNoFrom ?? '',
        supplierPrtNoTo: params.supplierPrtNoTo ?? '',
        ...pagingSortingToBackendRequest(params),
      },
    },
  );

  const resetFilters = () => {
    form.reset({
      partNoFrom: '',
      partNoTo: '',
      supplierPartNoFrom: '',
      supplierPrtNoTo: '',
    });
    setParams((prevParams) => ({
      ...prevParams,
      partNoFrom: '',
      partNoTo: '',
      supplierPartNoFrom: '',
      supplierPrtNoTo: '',
    }));
  };

  const articlesQuery = ArticlesQuery.useGetQuery(
    {
      lieferant: '99999',
      partNumberFrom: params.partNoFrom,
      partNumberTo: params.partNoTo,
      supplierPartNumberFrom: params.supplierPartNoFrom,
      supplierPartNumberTo: params.supplierPrtNoTo,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );
  useEffect(() => {
    if (articlesQuery.data) {
      setNumberOfPages(
        Math.ceil(
          (articlesQuery.data?.content.data.meta.rowCount ?? 0) /
            params.perPage,
        ),
      );
    }
  }, [params.perPage, articlesQuery?.data?.content.data.meta.rowCount]);

  useEffect(() => {
    params.page ??= 1;
    params.perPage ??= pageSizes[0];
    setParams(params, 'replace');
  }, []);

  useEffect(() => {
    if (params.page) {
      const newValue = numberOfPages >= params.page ? params.page : 1;

      setParams(
        (prevParams) => ({
          ...prevParams,
          page: newValue,
        }),
        'replace',
      );
    }
  }, [params, numberOfPages]);

  function getHeadField(field: string) {
    return getQueryHeadField(articlesQuery, field);
  }

  const gesamtnote = getHeadField('ytnote');
  const data = useMemo(() => {
    return convertAbasTable(articlesQuery.data?.content.data.table, {
      idField: 'tartnr',
    });
  }, [articlesQuery.data]);

  const onEditorPreparing = (e1: EditorPreparingEvent<any, number>) => {
    let dataField = e1.dataField!;
    if (dataField === 'tbestlief') {
      dataField = 'bstnr';
    } else if (dataField === 'tyrsichbestlief') {
      dataField = 'yrsichbestlief';
    }

    const id = e1.row?.data['_id'];
    const defaultOnValueChanged = e1.editorOptions.onValueChanged;
    e1.editorOptions.onValueChanged = async function (e: any) {
      defaultOnValueChanged(e);
      try {
        const patchDto: Record<string, any> = {
          [dataField]: e.value,
        };
        if (dataField === 'yrsichbestlief') {
          patchDto['ydsichbestdat'] = new Date().toISOString();
        }
        await ArticlesClient.patch(id, patchDto);
      } catch (ex) {
        alert('Error ' + errorToString(ex));
      }
    };
  };

  const onExporting = async () => {
    const doc = new jsPDF({
      orientation: 'landscape',
    });

    addHeader(doc, 'Item overview');

    const formValues = form.getValues();
    autoTable(doc, {
      alternateRowStyles: {
        fillColor: undefined,
      },
      styles: {
        textColor: 'black',
        cellPadding: 1,
      },
      tableWidth: 'wrap',
      body: [
        [fieldTitleCell('Supplier'), getHeadField('lieferant'), '', ''],
        [
          fieldTitleCell('Part No. from'),
          formValues.partNoFrom,
          fieldTitleCell('To'),
          formValues.partNoTo,
        ],
        [
          fieldTitleCell('Artikelnummer Lieferant von'),
          formValues.supplierPartNoFrom,
          fieldTitleCell('To'),
          formValues.supplierPrtNoTo,
        ],
        [fieldTitleCell('Supplier rating total'), gesamtnote],
      ],
    });
    const lastY = (doc as any).lastAutoTable.finalY;

    const columnWidths: number[] = [];
    columnsConfig.forEach((value, index) => {
      if (value.disableExport) columnWidths[index] = 0;
    });
    columnWidths[columnsConfig.length] = 0;

    let rowIndex = 0;
    await exportDataGrid({
      columnWidths: columnWidths,
      jsPDFDocument: doc,
      component: tableInstance?.(),
      topLeft: { x: 0, y: lastY - 5 },
      indent: 0,
      onRowExporting(options) {
        rowIndex++;
        if (rowIndex > 1 && rowIndex % 2 === 1) {
          options.rowCells!.forEach((cell) => {
            cell.backgroundColor = '#e5e5e5';
          });
        }
      },
    });
    doc.save('Articles.pdf');
  };

  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  const tableInstance = dataGridRef.current?.instance;

  const changePageSize = (value: number) => {
    tableInstance?.().pageSize(value);
    setParams((prevParams) => ({
      ...prevParams,
      page: 1,
      perPage: value,
    }));
    void tableInstance?.().pageIndex(0);
  };

  const paginationArray = useMemo(() => {
    const result: number[] = [];

    let numberOfMinPage = Math.max(
      params.page - MaxNumberOfShownPagesInPager,
      1,
    );
    let numberOfMaxPage = Math.min(
      numberOfMinPage + MaxNumberOfShownPagesInPager - 1,
      numberOfPages,
    );

    if (params.page >= numberOfMaxPage) {
      numberOfMaxPage = Math.min(numberOfPages, params.page + 1);
      if (numberOfMaxPage > MaxNumberOfShownPagesInPager) {
        numberOfMinPage = numberOfMaxPage - MaxNumberOfShownPagesInPager + 1;
      }
    }
    for (let i = numberOfMinPage; i <= numberOfMaxPage; i++) {
      result.push(i);
    }

    return result;
  }, [numberOfPages, params.page]);

  const goToFirstPage = () => {
    if (params.page > 1) {
      setParams((prevParams) => ({
        ...prevParams,
        page: 1,
      }));
    }
  };

  const goToPreviousPage = () => {
    if (params.page - 1 >= 1) {
      const newValue = params.page - 1;

      setParams((prevParams) => ({
        ...prevParams,
        page: newValue,
      }));
    }
  };

  const goToNextPage = () => {
    if (params.page + 1 <= numberOfPages) {
      const newValue = params.page + 1;

      setParams((prevParams) => ({
        ...prevParams,
        page: newValue,
      }));
    }
  };

  const goToLastPage = () => {
    if (params.page < numberOfPages) {
      const newValue = numberOfPages;

      setParams((prevParams) => ({
        ...prevParams,
        page: newValue,
      }));
    }
  };

  const goToCertainPage = (value: number) => {
    setParams((prevParams) => ({
      ...prevParams,
      page: value,
    }));
  };

  const paginationProps: PaginationProps = {
    pageSize: params.perPage,
    pageSizes,
    currentPage: params.page,
    changePageSize,
    goToCertainPage,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    numberOfPages,
    paginationArray,
    numberOfRows: data?.length ?? 0,
  };

  const handleOptionChange = (e: OptionChangedEvent) => {
    const sortData = sortHelper(e, params.desc, params.sortBy);

    if (sortData) {
      setParams((prevParams) => ({
        ...prevParams,
        sortBy: sortData.dataField,
        desc: sortData.isDesc,
      }));
    }
  };

  useEffect(() => {
    tableInstance?.().pageSize(params.perPage);
  }, [params.perPage]);

  return (
    <div>
      <Breadcrumbs>
        <Button icon={PrintIcon} title={'Print'} onClick={onExporting} />
      </Breadcrumbs>
      <Loading loading={articlesQuery.isFetching}>
        <form onSubmit={(e) => e.preventDefault()}>
          <div
            className={clsx(
              formStyles.rowContainer,
              formStyles.filterContainer,
            )}
          >
            <div className={formStyles.rowContainer}>
              <Input
                className={formStyles.inputWidth_115}
                leftHelperText={'Part No.'}
                {...form.register('partNoFrom')}
                placeholder={'from'}
              />
              <div>–</div>
              <Input
                className={formStyles.inputWidth_115}
                {...form.register('partNoTo')}
                placeholder={'to'}
              />
            </div>
            <div className={formStyles.rowContainer}>
              <Input
                className={formStyles.inputWidth_115}
                leftHelperText={'Supplier Part No.'}
                {...form.register('supplierPartNoFrom')}
                placeholder={'from'}
              />
              <div>–</div>
              <Input
                className={formStyles.inputWidth_115}
                {...form.register('supplierPrtNoTo')}
                placeholder={'to'}
              />
            </div>
            <ActionText
              onClickHandler={form.handleSubmitDefault}
              text={'Apply'}
            />
            <NegativeActionText
              text={'Reset'}
              onClickHandler={() => {
                resetFilters();
                void form.handleSubmitDefault();
              }}
            />
          </div>
          <div className={formStyles.gesamtnoteContainer}>
            <label className={formStyles.h4}>Gesamtnote:&nbsp;</label>
            {gesamtnote}
          </div>
        </form>
        <div>
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={data}
            className={clsx(gridStyles.editorOutline, formStyles.table)}
            onEditorPreparing={onEditorPreparing}
            onOptionChanged={handleOptionChange}
            sorting={{ mode: 'single' }}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            <DXG.Column
              width={40}
              cssClass={clsx(formStyles.rowIndex, formStyles.textCellPadding)}
              cellRender={(cell) => {
                return <div>{cell.row.loadIndex + 1}</div>;
              }}
            />
            {columnsConfig.map((x, index) => {
              const sortByField = params.sortBy || 'tartnr';
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={index}
                  sortOrder={
                    x.dataField === sortByField
                      ? params.desc
                        ? 'desc'
                        : 'asc'
                      : undefined
                  }
                  {...x}
                  showEditorAlways={x.allowEditing}
                />
              );
            })}
            <DXG.Column
              cssClass={formStyles.textCellPadding}
              minWidth={150}
              cellRender={(cell) => {
                return (
                  <AppLink
                    className={formStyles.link}
                    to={Links.Authorized.PlanChart.link({
                      product: cell.data.tartnr,
                    })}
                  >
                    Plan Chart
                  </AppLink>
                );
              }}
            />
            <Pager visible={false} />
            <DXG.Editing
              mode="cell"
              allowUpdating={true}
              allowAdding={false}
              allowDeleting={false}
            />
          </DXG.DataGrid>
          {data?.length && <Pagination {...paginationProps} />}
        </div>
      </Loading>
    </div>
  );
};

const columnsConfig: (IColumnProps & { disableExport?: boolean })[] = [
  {
    dataField: 'tartnr',
    allowEditing: false,
    caption: 'Part No.',
    cssClass: formStyles.textCellPadding,
    minWidth: 130,
  },
  {
    dataField: 'tbestlief',
    allowEditing: true,
    caption: 'Supplier Part No.',
    cssClass: clsx(formStyles.inputCellPadding, formStyles.textAlign),
    minWidth: 200,
  },
  {
    dataField: 'tartname',
    allowEditing: false,
    caption: 'Part Description',
    minWidth: 270,
    cssClass: formStyles.textCellPadding,
  },
  {
    // dataField: 'tbestlief',
    allowEditing: false,
    caption: 'Index',
    disableExport: true,
    cssClass: formStyles.textCellPadding,
    minWidth: 100,
  },
  {
    dataField: 'yisper',
    allowEditing: false,
    caption: 'Status',
    cssClass: formStyles.textCellPadding,
    minWidth: 150,
  },
  {
    dataField: 'tbfrist',
    allowEditing: false,
    caption: 'Leadtime',
    cssClass: formStyles.textCellPadding,
    minWidth: 150,
  },
  {
    // dataField: 'tbestlief',
    allowEditing: false,
    caption: 'Your rating',
    disableExport: true,
    cssClass: formStyles.textCellPadding,
    minWidth: 100,
  },
  {
    // dataField: 'tbestlief',
    allowEditing: false,
    caption: 'agreed safety stock',
    disableExport: true,
    cssClass: formStyles.textCellPadding,
    minWidth: 150,
  },
  {
    dataField: 'tyrsichbestlief',
    // dataField: 'tyrsichbestlmt',
    allowEditing: true,
    dataType: 'number',
    caption: 'counted safety stock',
    disableExport: true,
    cssClass: clsx(formStyles.inputCellPadding, formStyles.textAlign),
    minWidth: 210,
  },
  {
    dataField: 'tydsichbestdat',
    allowEditing: false,
    caption: 'date of counting',
    disableExport: true,
    cssClass: formStyles.textCellPadding,
    minWidth: 200,
  },
];
